import * as SPREAD_CONFIG_LIQSRC from '@components/model-structure/liquidity-sources/liquidity-sources.spread.config';
import * as GC from '@grapecity/spread-sheets';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { ErrorsView } from '@models/scenario-error';
import { ResultsStoreService } from '@services/results/results-store.service';
import { getFont } from '@shared/spreadsheet/spreadsheet-config';
import { logError } from '../result-errors';
import { FAIL_COLOR, FAIL_TEXT, PASS_COLOR, PASS_TEXT, WARNING_COLOR, WARNING_TEXT } from './results-spread-config';

let resultsStore: ResultsStoreService;

export function addLiquidityToSheet(
  currentRow: number,
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  liquidities: any[],
  type: number,
  IgnoreErrors: boolean,
  Survival_Horizon: number,
  _resultsStore: ResultsStoreService,
  resultSetID: number,
  mnemonic: string
) {
  if (!resultsStore) resultsStore = _resultsStore;

  const acceptedTypes = SPREAD_CONFIG_LIQSRC.SpreadConfig.LIQ_SOURCES_ACCEPTED_TYPE_VALUES;
  const isSummaryView = workSheet.name().includes('-summary');
  const srcType = type > 0 ? acceptedTypes[type - 1] : '';
  const column = isSummaryView ? 1 : 4; // For summary & details tabs
  let startRow = 0;
  let count = 0;
  currentRow++;
  let groupings = { lvl2Start: -1, lvl3Start: -1, lvl2End: -1, lvl3End: -1 }; // Initial state

  if (liquidities && Object.keys(liquidities).length > 0) {
    const liqSourcesFont = {
      title: getFont('x-sm', true),
      children: getFont('x-sm', false, true),
    };
    const title = `${srcType} Liquidity Sources`; // :'Primary Liquidity Sources';

    const liqSummaryRemainingBalanceRange = {
      start: -1,
      end: -1,
    };

    // Type Title
    const cell = workSheet.getCell(currentRow, column);
    cell.value(title);
    cell.font(liqSourcesFont.title);
    // if (!isSummaryView) workSheet.addSpan(currentRow, column, 1, 2, GC.Spread.Sheets.SheetArea.viewport);

    // Add a blank row between the Liquidity Source Type title and the accounts
    if (!isSummaryView) currentRow++;

    currentRow++;
    startRow = currentRow;

    // Total Available from each Liquidity Source (not displayed in Summary)
    if (!isSummaryView) {
      for (const key of Object.keys(liquidities)) {
        if (liquidities[key].Type === srcType) {
          if (liquidities[key].IsTotal) {
            // Grouping Range Level 3 End for this group and set-up for Total Available
            if (!isSummaryView) {
              groupings.lvl3End = currentRow;

              if (groupings.lvl3Start > -1 && groupings.lvl3End > -1) {
                count = groupings.lvl3End - groupings.lvl3Start;
                SheetFunctions.addRowRange(workSheet, groupings.lvl3Start, count);
                // Reset Level 3
                groupings = { ...groupings, lvl3Start: -1, lvl3End: -1 };
              }
            }

            const newRow = displayTotalBalance(currentRow, spread, workSheet, liquidities[key], 1);
            currentRow = newRow;
          } else {
            // Grouping Range Level 2 Start & Grouping Range Level 3 Start
            // -- in the position of the first element before the Total Available
            if (!isSummaryView && groupings.lvl2Start === -1) groupings.lvl2Start = currentRow;
            if (!isSummaryView && groupings.lvl3Start === -1) groupings.lvl3Start = currentRow;

            workSheet.setValue(currentRow, column + 1, liquidities[key].Name);
            workSheet.getCell(currentRow, column + 1).font(liqSourcesFont.children);
            // workSheet.addSpan(currentRow, column + 1, 1, 3, GC.Spread.Sheets.SheetArea.viewport);

            const newRow = displayAvailableBalance(
              currentRow,
              spread,
              workSheet,
              liquidities[key],
              IgnoreErrors,
              resultsStore,
              resultSetID,
              mnemonic
            );
            currentRow = newRow;
          }
        }
      }

      // Used Balance from each Liquidity Source
      for (const key of Object.keys(liquidities)) {
        if (liquidities[key].Type === srcType) {
          if (liquidities[key].IsTotal) {
            // Grouping Range Level 3 End for this group and set-up for Total Used
            if (!isSummaryView) {
              groupings.lvl3End = currentRow;

              if (groupings.lvl3Start > -1 && groupings.lvl3End > -1) {
                count = groupings.lvl3End - groupings.lvl3Start;
                SheetFunctions.addRowRange(workSheet, groupings.lvl3Start, count);
                // Reset Level 3
                groupings = { ...groupings, lvl3Start: -1, lvl3End: -1 };
              }
            }
            const newRow = displayTotalBalance(currentRow, spread, workSheet, liquidities[key], 2);
            currentRow = newRow;
          } else {
            // Grouping Range Level 3 Start for this group
            // -- in the position of the first element previous to the Total Used
            if (!isSummaryView && groupings.lvl3Start === -1) groupings.lvl3Start = currentRow;

            workSheet.setValue(currentRow, column + 1, liquidities[key].Name);
            workSheet.getCell(currentRow, column + 1).font(liqSourcesFont.children);
            // workSheet.addSpan(currentRow, column + 1, 1, 3, GC.Spread.Sheets.SheetArea.viewport);
            const newRow = displayUsedBalance(currentRow, spread, workSheet, liquidities[key], IgnoreErrors, resultSetID, mnemonic);
            currentRow = newRow;
          }
        }
      }
    }

    // Remaining Balance from each Liquidity Source
    for (const key of Object.keys(liquidities)) {
      if (liquidities[key].Type === srcType) {
        if (liquidities[key].IsTotal) {
          const liqSecondaryOrTertiary: any = type === 1 ? undefined : liquidities[acceptedTypes[type - 2]];

          // Grouping Range Level 3 End for this group and set-up
          if (!isSummaryView) {
            groupings.lvl3End = currentRow;

            if (groupings.lvl3Start > -1 && groupings.lvl3End > -1) {
              count = groupings.lvl3End - groupings.lvl3Start;
              SheetFunctions.addRowRange(workSheet, groupings.lvl3Start, count);
              // Reset Level 3
              groupings = { ...groupings, lvl3Start: -1, lvl3End: -1 };
            }
          }

          // Total Remaining
          const newRowAfterTotalBalance = displayTotalBalance(currentRow, spread, workSheet, liquidities[key], 3);
          currentRow = newRowAfterTotalBalance;

          // Grouping Range Level 2 End
          if (!isSummaryView) {
            groupings.lvl2End = currentRow;

            // Grouping Range Level 2 Set for Details
            if (groupings.lvl2Start > -1 && groupings.lvl2End > -1) {
              count = groupings.lvl2End - groupings.lvl2Start;
              SheetFunctions.addRowRange(workSheet, groupings.lvl2Start, count);
              // Reset Level 2
              groupings = { ...groupings, lvl2Start: -1, lvl2End: -1 };
            }
          }

          // Survival Horizon
          // ----------------
          const newRowAfterSurvivalHorizon = calculateSurvivalHorizon(
            currentRow,
            workSheet,
            liquidities[key],
            type,
            Survival_Horizon,
            liqSecondaryOrTertiary
          );
          currentRow = newRowAfterSurvivalHorizon;
        } else if (isSummaryView) {
          // Grouping Range Level 3 End for this group and set-up for Total Available
          if (!isSummaryView) {
            groupings.lvl3End = currentRow;

            if (groupings.lvl3Start > -1 && groupings.lvl3End > -1) {
              count = groupings.lvl3End - groupings.lvl3Start;
              SheetFunctions.addRowRange(workSheet, groupings.lvl3Start, count);
              // Reset Level 3
              groupings = { ...groupings, lvl3Start: -1, lvl3End: -1 };
            }
          }

          // Type of Liq.Source (Name)
          workSheet.setValue(currentRow, column + 2, liquidities[key].Name);
          workSheet.getCell(currentRow, column + 2).font(liqSourcesFont.children);

          // Total Remaining Balance row
          if (isSummaryView && !(liqSummaryRemainingBalanceRange.start > -1)) {
            liqSummaryRemainingBalanceRange.start = currentRow;
          }

          const newRow = displayRemainingBalance(currentRow, spread, workSheet, liquidities[key], IgnoreErrors, resultSetID, mnemonic);
          currentRow = newRow;
          if (isSummaryView) {
            // Update the value of end with each iteration
            liqSummaryRemainingBalanceRange.end = currentRow;
          }
        } else {
          // Type of Liq.Source (Name)
          workSheet.setValue(currentRow, column + 1, liquidities[key].Name);
          workSheet.getCell(currentRow, column + 1).font(liqSourcesFont.children);
          // workSheet.addSpan(currentRow, columnShift + 1, 1, 3, GC.Spread.Sheets.SheetArea.viewport);

          // Grouping Range Level 3 Start for this group
          // -- in the position of the first element previous to the Total Remaining
          if (!isSummaryView && groupings.lvl3Start === -1) groupings.lvl3Start = currentRow;

          // Total Remaining Balance row
          const newRow = displayRemainingBalance(currentRow, spread, workSheet, liquidities[key], IgnoreErrors, resultSetID, mnemonic);
          currentRow = newRow;
        }
      }
    }

    // Grouping Range for Summary
    // --------------------------
    if (isSummaryView) {
      // TODO: Refactorize It to fit the groupings approach
      // if (isSummaryView && liqSummaryRemainingBalanceRange.end > -1)
      count = currentRow - startRow;
      if (count > 1) {
        SheetFunctions.addRowRange(workSheet, liqSummaryRemainingBalanceRange.start, count - 1);
      }
    }
  }

  return currentRow;
}

function calculateSurvivalHorizon(
  currentRow: number,
  workSheet: GC.Spread.Sheets.Worksheet,
  liq: any,
  type: number,
  Survival_Horizon: number,
  liqSecondaryOrTertiary: any
) {
  let fail = '';
  let color = '';
  const isSummaryView = workSheet.name().includes('-summary');
  const acceptedTypes = SPREAD_CONFIG_LIQSRC.SpreadConfig.LIQ_SOURCES_ACCEPTED_TYPE_VALUES;
  const title = `${type > 0 ? acceptedTypes[type - 1] : ''} Survival Horizon…`;
  const column = isSummaryView ? 2 : 4;
  const outPropColumn = isSummaryView ? column : column + 1;
  const cellFont = getFont('x-sm', true);

  if (isSummaryView) {
    workSheet.setValue(currentRow, column, title);
    workSheet.getCell(currentRow, column).font(cellFont);
  }

  let k = 0;
  for (const res of Object.keys(liq.Results)) {
    if (liq.Results[res].Period !== 0) {
      if (liq.Results[res].Pass) {
        const previousPass: boolean =
          liqSecondaryOrTertiary === undefined ? true : liq.Results[res].UsedBalance === 0 && liqSecondaryOrTertiary.Results[res].Pass;
        if (liq.Results[res].RemainingBalance > Survival_Horizon || previousPass) {
          fail = PASS_TEXT;
          color = PASS_COLOR;
        } else {
          fail = WARNING_TEXT;
          color = WARNING_COLOR;
        }
      } else {
        fail = FAIL_TEXT;
        color = FAIL_COLOR;
      }
      workSheet.setValue(currentRow, outPropColumn + 3 + k, fail);
      workSheet.getCell(currentRow, outPropColumn + 3 + k).backColor(color);
      const cell = workSheet.getCell(currentRow, outPropColumn + 3 + k, GC.Spread.Sheets.SheetArea.viewport);
      cell.hAlign(GC.Spread.Sheets.HorizontalAlign.center);

      k++;
    }
  }
  if (!isSummaryView) {
    currentRow++;
  }
  return currentRow;
}

function displayAvailableBalance(
  currentRow: number,
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  liquiditySource: any,
  IgnoreErrors: boolean,
  _resultsStore: ResultsStoreService,
  resultSetID: number,
  mnemonic: string
) {
  const isSummaryView = workSheet.name().includes('-summary');
  const outPropColumn = isSummaryView ? 3 : 4;

  // ! Related: Task 4181
  if (!isSummaryView) {
    const balanceSheetItemMnemonic = liquiditySource?.Mnemonic;
    workSheet.getCell(currentRow, 0).value(balanceSheetItemMnemonic);
    workSheet.getCell(currentRow, 1).value('TotAvailable'); // ? shall be AvailableBalance instead ?
  }

  let k = 0;
  const rounding = isSummaryView ? true : false;
  for (const res of Object.keys(liquiditySource.Results)) {
    const value = liquiditySource.Results[res].AvailableBalance; // :primary[key].Results[res].RemainingBalance;
    const cell = workSheet.getCell(currentRow, outPropColumn + 3 + k);
    // Accounting formatting
    SheetFunctions.setOutputReportNumberFormatting(spread, workSheet, currentRow, outPropColumn + 3 + k, value, null, rounding);
    resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);

    if (!IgnoreErrors && !isSummaryView) {
      checkLiquidityError(workSheet, currentRow, outPropColumn + 3 + k, liquiditySource, res, resultSetID, mnemonic);
    }
    k++;
  }
  currentRow++;
  return currentRow;
}

function displayUsedBalance(
  currentRow: number,
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  liq: any,
  IgnoreErrors: boolean,
  resultSetID: number,
  mnemonic: string
) {
  const isSummaryView = workSheet.name().includes('-summary');
  const outPropColumn = isSummaryView ? 6 : 7;

  // ! Related: Task 4181
  if (!isSummaryView) {
    const balanceSheetItemMnemonic = liq?.Mnemonic;
    workSheet.getCell(currentRow, 0).value(balanceSheetItemMnemonic);
    workSheet.getCell(currentRow, 1).value('TotUsed'); // ? shall be UsedBalance instead ?
  }

  const rounding = isSummaryView ? true : false;
  Object.keys(liq.Results).forEach((res, k) => {
    const value = liq.Results[res].UsedBalance; // :primary[key].Results[res].RemainingBalance;
    const column = outPropColumn + k;

    // Accounting formatting
    const cell = workSheet.getCell(currentRow, column);
    SheetFunctions.setOutputReportNumberFormatting(spread, workSheet, currentRow, column, value, null, rounding);
    resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);

    if (!IgnoreErrors && !isSummaryView) {
      checkLiquidityError(workSheet, currentRow, column, liq, res, resultSetID, mnemonic);
    }
  });
  currentRow++;
  return currentRow;
}

function displayRemainingBalance(
  currentRow: number,
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  liq: any,
  IgnoreErrors: boolean,
  resultSetID: number,
  mnemonic: string
) {
  const isSummary = workSheet.name().includes('-summary');
  const rounding = isSummary ? true : false;
  const outPropColumn = isSummary ? 4 : 7;

  // ! Related: Task 4181
  if (!isSummary) {
    const balanceSheetItemMnemonic = liq?.Mnemonic;
    workSheet.getCell(currentRow, 0).value(balanceSheetItemMnemonic);
    workSheet.getCell(currentRow, 1).value('TotRemaining'); // ? shall be RemainingBalance instead ?
  }

  Object.keys(liq.Results).forEach((res, k) => {
    const value = liq.Results[res].RemainingBalance; // :primary[key].Results[res].RemainingBalance;
    const column = outPropColumn + k;
    // Don't display period 0 in the summary
    if (!isSummary || (isSummary && k > 0)) {
      // Accounting formatting
      const cell = workSheet.getCell(currentRow, column);
      SheetFunctions.setOutputReportNumberFormatting(spread, workSheet, currentRow, column, value, null, rounding);
      resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);

      if (!IgnoreErrors && !isSummary) {
        checkLiquidityError(workSheet, currentRow, column, liq, res, resultSetID, mnemonic);
      }
    }
  });
  currentRow++;
  return currentRow;
}

// Used in Summary & Detail views
function displayTotalBalance(
  currentRow: number,
  spread: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  liq: any,
  totalType: number
) {
  let cell;
  const isSummaryView = workSheet.name().includes('-summary');
  const title = totalType === 1 ? 'Total Available' : totalType === 2 ? 'Total Used' : 'Total Remaining';
  const shortTitle = totalType === 1 ? 'TotAvailable' : totalType === 2 ? 'TotUsed' : 'TotRemaining';
  const column = !isSummaryView ? 5 : 2;
  const outPropColumn = !isSummaryView ? column + 2 : column + 2;

  // ! Related: Task 4181
  if (!isSummaryView) {
    const balanceSheetItemMnemonic = liq?.Mnemonic;
    workSheet.getCell(currentRow, 0).value(balanceSheetItemMnemonic);
    workSheet.getCell(currentRow, 1).value(shortTitle); // ? shall be ${title} instead ?
  }

  // Title
  cell = workSheet.getCell(currentRow, column);
  workSheet.setValue(currentRow, column, title);
  cell.font(getFont('x-sm', true));
  // ! Removed for both pages
  // workSheet.addSpan(currentRow, column, 1, 2, GC.Spread.Sheets.SheetArea.viewport);

  // Border
  if (!isSummaryView) {
    for (let i = 0; i < 3; i++) {
      cell = workSheet.getCell(currentRow, column + i, GC.Spread.Sheets.SheetArea.viewport);
      cell.borderTop(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.medium));
    }
  }

  // Values
  const rounding = isSummaryView;
  Object.keys(liq.Results).forEach((res, k) => {
    const value =
      totalType === 1
        ? liq.Results[res].AvailableBalance
        : totalType === 2
        ? liq.Results[res].UsedBalance
        : liq.Results[res].RemainingBalance;

    // Don't display period 0 in the summary
    if (!isSummaryView || (isSummaryView && k > 0)) {
      // Accounting Formatting
      cell = workSheet.getCell(currentRow, outPropColumn + k);
      SheetFunctions.setOutputReportNumberFormatting(
        spread,
        workSheet,
        currentRow,
        outPropColumn + k,
        // numeric value
        Number(value),
        null,
        rounding
      );
      resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);

      // Totals border-top
      if (!isSummaryView) {
        cell = workSheet.getCell(currentRow, outPropColumn + k);
        cell.borderTop(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.medium));
      }
    }
  });
  // margin-bottom
  currentRow += isSummaryView ? 1 : 2;

  return currentRow;
}

function checkLiquidityError(
  workSheet: GC.Spread.Sheets.Worksheet,
  row: number,
  column: number,
  liq: any,
  res: string,
  resultSetID: number,
  mnemonic: string
) {
  if (liq.Results[res].HasError) {
    const error = new ErrorsView();
    for (const errorCode of liq.Results[res].ErrorCodes) {
      error.column = column;
      error.row = row;
      SheetFunctions.addComment(workSheet, row, column, errorCode);
      error.message = errorCode;
      logError(error, null, liq.Mnemonic, liq.Results[res].period, errorCode, '', 2, resultSetID, mnemonic);
    }
  }
}
